import toast, { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { SMSConfigSchema } from "../../../schemas/smsConfigSchema";
import { useCallback, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import TextAreaInput from "../../../components/inputs/TextAreaInput";
import CheckboxInput from "../../../components/inputs/CheckboxInput";
import { Button } from "../../../components/button";
import { API_ERRORS, SMSConfigCheckboxItems, SMSTemplateFields } from "../../../constants/common";
import TextInput from "../../../components/inputs/TextInput";
import { useTestSms, useUpdateSmsConfig } from "../../../hooks/common/mutation";
import { UpdateSMSConfigBody } from "../../../hooks/common/interface";
import { useGetSMSConfig } from "../../../hooks/common/query";
import StatusCodes from "../../../constants/StatusCodes";

const SMSConfig = () => {
  const { t, i18n } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    resetField,
    setValue,
  } = useFormWithSchema(SMSConfigSchema);

  const { data: smsConfig } = useGetSMSConfig();

  const { mutateAsync: UpdateSmsConfig, isPending: isUpdateSmsConfigLoading } =
    useUpdateSmsConfig();

  const { mutateAsync: TestSms, isPending: isTestSmsLoading } =
    useTestSms();

  const [configItems, setConfigItems] = useState<
    {
      id: number;
      name: string;
      titleEn?: string;
      titleAr?: string;
      checked?: boolean;
    }[]
  >(SMSConfigCheckboxItems);

  useEffect(() => {
    if (!smsConfig) return;

    SMSTemplateFields.forEach((field) => {
      setValue(field.name, smsConfig[`smsTemplate_${field.name}`] || "");
    });

    setConfigItems(
      SMSConfigCheckboxItems.map((item) => ({
        ...item,
        checked: !!smsConfig[`sendSMS_${item.name}`],
      }))
    );
  }, [smsConfig, setValue]);

  const onSubmit = useCallback(
    async (data: Yup.Asserts<typeof SMSConfigSchema>) => {
      try {
        const body = {} as UpdateSMSConfigBody;

        SMSConfigCheckboxItems.forEach((item) => {
          body[`sendSMS_${item.name}`] = !!configItems.find(
            (configItem) => configItem.id === item.id
          )?.checked;
          body[`smsTemplate_${item.name}`] = data[
            item.name as keyof typeof data
          ] as string;
        });

        await UpdateSmsConfig(body);
        toast.success(t("smsConfigUpdated"));
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }
    },
    [configItems, t, UpdateSmsConfig]
  );

  const handleCheckboxChange = useCallback((id: number) => {
    setConfigItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, checked: !item.checked } : item
      )
    );
  }, []);

  const onClear = useCallback(() => {
    SMSTemplateFields.forEach((field) => resetField(field.name));
  }, [resetField]);

  const onTestSMS = useCallback(
    async (data: { testSms?: string }) => {
      try {
        if (!data.testSms) {
          toast.error(t("testSmsRequired"));
          return;
        }

        const body = { mobileNumber: data.testSms };
        await TestSms(body);
        toast.success(t("smsSent"));
      } catch (error: any) {
        const { errors } = error;
        const errorCode = errors && Object.keys(errors)[0];

        if (errorCode === API_ERRORS.INVALID_MOBILE_NUMBER) {
          toast.error(t("invalidMobileNumber"));
        } else if (error.statusCode === StatusCodes.INTERNAL_SERVER_ERROR) {
          toast.error(t("smsSendError"));
        } else {
          toast.error(t("somethingWentWrong"))
        }
      }
    },
    [TestSms, t]
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <h1 className="text-xl md:text-2xl dark:text-white">
          {t("smsConfig")}
        </h1>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-8 bg-white p-4 rounded-xl  shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]"
        >
          <div className=" grid md:grid-cols-2 gap-3 lg:gap-6">
            {SMSTemplateFields.map((field) => (
              <Controller
                key={field.name}
                name={field.name}
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextAreaInput
                    label={i18n.language === "en" ? field.titleEn : field.titleAr}
                    onChange={onChange}
                    value={value || ""}
                    error={errors?.[name]?.message}
                    required
                  />
                )}
              />
            ))}
          </div>

          <div className="flex flex-col gap-2">
            {configItems.map((item: any) => (
              <CheckboxInput
                key={item.id}
                id={item.id.toString()}
                label={i18n.language === "en" ? item.titleEn : item.titleAr}
                checked={item.checked}
                setChecked={() => handleCheckboxChange(item.id)}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Button isLoading={isUpdateSmsConfigLoading}>{t("save")}</Button>
            <Button
              type="button"
              onClick={onClear}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("clear")}
            </Button>
          </div>

          <div className="flex flex-col gap-4">
            <Controller
              name="testSms"
              control={control}
              render={({ field: { onChange, name, value } }) => {
                return (
                  <TextInput
                    label={t("testSMS")}
                    placeholder={t("enterMobileNumber")}
                    onChange={onChange}
                    value={value || ""}
                    error={errors?.[name]?.message}
                  />
                );
              }}
            />

            <Button
              type="button"
              isLoading={isTestSmsLoading}
              onClick={handleSubmit(async (data) => onTestSMS(data))}
              className="max-w-max bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("testSMS")}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default SMSConfig;
